import React, { useState, useEffect, useMemo } from 'react';
import {
  FolderOpen,
  UsersThree,
  Slideshow,
  MonitorArrowUp,
  Database,
  Video,
  VideoCamera,
  Play,
  HandPointing,
  CurrencyDollar,
} from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../../components/general';
import { PageWrapper, ContentHeader } from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL } from '../../../constants';

import { getCreationResults } from '../../../services/api/creations';
import { getUploadedFaces } from '../../../services/api/faces';
import { getClipFiles } from '../../../services/api/files';
import { getTemplateFile } from '../../../services/api/template';

import { useProjectStore } from '../../../stores/project';

import { File, HlReel, Person, PageError } from '../../../types';
import { isEmpty, getPct, getDuration } from '../../../utils';

import { calculateClipsStats, calculateKeyStats } from './components/getStats';
import StatsWithListBlock from './components/statsWithListBlock';

const ProjectAnalytics = () => {
  const { eventId = '' } = useParams();

  const {
    eventId: eventIdLocal,
    setEventId,
    currentProject,
    projectError,
  } = useProjectStore();

  const [uploadedPeople, setUploadedPeople] = useState<Person[]>([]);
  const [generatedPeople, setGeneratedPeople] = useState<Person[]>([]);
  const [uploadedClips, setUploadedClips] = useState<File[]>([]);
  const [hlReel, setHlReel] = useState<HlReel | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  const { isPayments, isDonation } = useMemo(() => {
    const paymentObj = currentProject?.payment;

    return {
      isPayments: !paymentObj?.paid || false,
      isDonation: (paymentObj?.paid && paymentObj?.allowDonations) || false,
    };
  }, [currentProject]);

  // set event id on mount
  useEffect(() => {
    if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      try {
        setHlReel(await getTemplateFile(eventId));
        setUploadedClips(await getClipFiles(eventId));
        setUploadedPeople(await getUploadedFaces(eventId));
        setGeneratedPeople(await getCreationResults(eventId));
      } catch {
        setError('Error fetching data');
      } finally {
        setIsLoading(false);
      }
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
    } else if (currentProject) onMount();
  }, [eventId, currentProject, projectError]);

  const { clipsSize, clipsDuration } = useMemo(
    () => calculateClipsStats(uploadedClips),
    [uploadedClips],
  );

  const {
    fpDataCount,
    playsTotal,
    topEngPeople,
    engPct,
    payees,
    paymentSum,
    paymentStyles,
  } = useMemo(
    () => calculateKeyStats(generatedPeople, hlReel, isDonation),
    [generatedPeople, hlReel, isDonation],
  );

  const keyData = [
    {
      heading: 'Data Gathered',
      blocks: [
        {
          Icon: FolderOpen,
          num:
            uploadedPeople.length +
            uploadedClips.length +
            (!isEmpty(hlReel) ? 1 : 0),
          label: 'Files Ingested',
        },
        {
          Icon: UsersThree,
          num: uploadedPeople.length,
          label: 'People Added',
        },
        {
          Icon: Slideshow,
          num:
            getDuration(hlReel?.parameters?.length || 0, false, true) ||
            '0 min',
          label: 'Highlight Reel Uploaded',
        },
        {
          Icon: MonitorArrowUp,
          num: `${clipsDuration} (${clipsSize} GB)`,
          label: 'Footage Ingested',
        },
        {
          Icon: Database,
          num: fpDataCount,
          label: 'First-Party Data Captured',
        },
      ],
    },
    {
      heading: 'Outcomes Achieved',
      blocks: [
        {
          Icon: Video,
          num: `${getPct(generatedPeople.length / uploadedPeople.length) || 0}%`,
          label: 'Videos Captured',
        },
        {
          Icon: VideoCamera,
          num: generatedPeople.length,
          label: 'Videos Created',
        },
        {
          Icon: Play,
          num: getDuration(playsTotal, true),
          label: 'New Assets Played',
        },
        {
          Icon: HandPointing,
          num: `${engPct}%`,
          label: 'Videos Engaged',
        },
        {
          Icon: CurrencyDollar,
          num: `${getPct(payees.length / generatedPeople.length) || 0}%`,
          label: 'Attendees Paid',
          show: isPayments,
        },
        {
          Icon: CurrencyDollar,
          num: `$${paymentSum}`,
          label: 'Donations Raised',
          show: isDonation,
        },
      ],
    },
  ];

  return (
    <PageWrapper
      type="project"
      title={`Project Analytics – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
      className="gap-6 sm:gap-10"
    >
      <ContentHeader title="Project Analytics" />

      {isEmpty(generatedPeople) ? (
        <Alert
          alertData={{
            variant: 'warning',
            text: (
              <>
                Hmm, seems like you haven't completed the project yet. Analytics
                will be available as soon as you complete all{' '}
                <Link to={`/${eventId}${URL.VIDEO_BUILDER}`}>
                  Video Builder
                </Link>{' '}
                steps.
              </>
            ),
          }}
        />
      ) : (
        <React.Fragment>
          <div className="flex flex-col p-4 gap-8 rounded-md border border-grey-200 border-solid">
            {keyData.map((data) => (
              <div key={data.heading} className="flex flex-wrap gap-4 sm:gap-8">
                <div className="w-full sm:w-[12%] self-center text-center text-xl font-bold">
                  {data.heading}
                </div>

                {data.blocks.map(
                  (block) =>
                    block.show !== false && (
                      <div
                        key={block.label}
                        className="w-[calc((100%_-_1rem)_/_2)] sm:w-[calc((88%_-_(2rem_*_5))_/_5)] flex flex-col items-center text-center"
                      >
                        <block.Icon
                          weight="fill"
                          className="w-16 h-16 fill-[url('#icon-primary-gradient')]"
                        />

                        <div className="text-lg font-bold text-primary-900">
                          {block.num}
                        </div>

                        <div className="text-sm">{block.label}</div>
                      </div>
                    ),
                )}
              </div>
            ))}
          </div>

          <StatsWithListBlock
            heading="Engagement"
            generatedPeople={generatedPeople}
            statsBlockShowPages={['played', 'downloaded', 'shared']}
            listHeading="Most engaged attendees"
            listEmptyText="No engagements made"
            list={topEngPeople}
            listClass="sm:h-[calc((0.25rem_*_4)_+_(0.875rem_*_1.5_*_5))]"
            itemClass="w-full sm:w-[calc((100%_-_2rem)_/_2)]"
          />

          {(isPayments || isDonation) && (
            <StatsWithListBlock
              heading={isPayments ? 'Remove Watermark Payments' : 'Donations'}
              generatedPeople={generatedPeople}
              statsBlockShowPages={['payment']}
              listHeading="List of payments"
              listEmptyText="No payments made"
              list={payees}
              listStyle={paymentStyles.payeesContainer}
              itemStyle={paymentStyles.payeeBox}
            />
          )}
        </React.Fragment>
      )}

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default ProjectAnalytics;
